// Turns a stored web address into an href a browser can resolve. This is the
// MIRROR, not the source of truth — app/services/external_url.rb holds that,
// because normalizing at write is the primary fix. A scheme-less string like
// "linkedin.com/in/akua-serwaa" is not a URL — it is a relative path, so the
// browser resolves it against the current article path and the reader lands on
// the 404 page (bug #539).
//
// MIRROR: app/services/external_url.rb implements this exact rule table and is
// the primary fix — it normalizes at write, which repairs every reader at once.
// This file is the secondary defense for rows written by direct SQL, by
// lib/tasks/import_bubble.rake, or by a future integration that skips the
// model, and for anchors already embedded in migrated article body HTML.
// Change one, change the other, and keep the rule tables aligned.
//
// Like the Ruby side this never throws and never rewrites a non-http scheme: a
// javascript: or data: payload is returned unchanged rather than disguised as
// something safe, and DOMPurify is what strips it.

// A real scheme, not merely a colon. RFC 3986 permits a dot inside a scheme,
// but no scheme this app sees uses one while a host:port prefix always does —
// so a dot disqualifies the match and "foo.com:8080/x" still gets a scheme.
const SCHEME = /^([a-z][a-z0-9+-]*):/i

// https:// and not http:// — LinkedIn, X, Facebook, Instagram and YouTube are
// all HSTS-preloaded, so http:// costs every reader a redirect hop.
const DEFAULT_SCHEME = "https://"

export function normalizeExternalHref(value) {
  if (typeof value !== "string") return value

  const trimmed = value.trim()
  // The one deliberate divergence from the Ruby mirror: it returns nil for a
  // blank value because `allow_blank` wants nil, while an href wants a string.
  if (trimmed === "") return value

  const match = trimmed.match(SCHEME)
  if (match) {
    const scheme = match[1].toLowerCase()
    if (scheme !== "http" && scheme !== "https") return trimmed

    // Schemes are case-insensitive; paths are not. Downcase the scheme only.
    return trimmed.replace(SCHEME, `${scheme}:`)
  }

  // Protocol-relative, the same rule bbcode-to-html.js applies to [img] srcs.
  if (trimmed.startsWith("//")) return `${DEFAULT_SCHEME}${trimmed.slice(2)}`

  // A relative reference — "/uploads/a.pdf", "./headshot.png", "#section",
  // "?page=2" — is already resolvable and must never have a host bolted onto
  // it. The DOMPurify hook runs over every anchor in every article body, so
  // internal asset links reach this function too.
  if (/^[/.#?]/.test(trimmed)) return trimmed

  // A bare slug has no host to prepend a scheme to. Guessing a LinkedIn path
  // produces a working link to the wrong person, so leave it alone.
  if (!trimmed.includes(".")) return trimmed

  return `${DEFAULT_SCHEME}${trimmed}`
}
